import { useClientApi, useContentResource, useTracker } from "@ctv/core";
import { Button, Input, Token, View } from "@ctv/shared-core/src";
import {
  isEmail,
  maxLength,
  required,
} from "@ctv/shared-core/src/utils/validation";
import { useDispatchSnackbar } from "@ctv/shared/contexts/SnackbarContext";
import { useState } from "react";
import { FormProvider, useController, useForm } from "react-hook-form";
import { StyleSheet } from "react-native";
import {
  PREREGISTATION_API,
  PreregistrationRequest,
  PreregistrationResponse,
} from "../apis";
import ErrorMessage from "./ErrorMessage";

import EmailIcon from "@traveloka/icon-kit-web/react/IcContactMail";

type EmailFormValue = { email: string };

export default function SendEmailForm() {
  const cr = useContentResource().CorporateRegistration;

  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const openSnackbar = useDispatchSnackbar();
  const track = useTracker("ors-page");
  const methods = useForm<EmailFormValue>();
  const { control, handleSubmit: formSubmit, setError } = methods;
  const {
    field: { onChange, onBlur },
    fieldState: { error },
  } = useController({
    control,
    name: "email",
    rules: {
      validate(v) {
        if (required(v) === false) {
          return cr.picEmailRequired;
        } else if (maxLength(v, 256) === false) {
          return cr.picEmailMaxLength;
        } else if (isEmail(v) === false) {
          return cr.picEmailFormat;
        }
        return;
      },
    },
  });

  const sendEmail = useClientApi<
    PreregistrationResponse,
    PreregistrationRequest
  >({
    domain: "management",
    method: "post",
    path: PREREGISTATION_API,
  });

  async function handleSubmit(values: EmailFormValue) {
    setLoading(true);

    const res = await sendEmail({
      picEmail: values.email,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });

    if (res.success) {
      if (res.data.status === "OK") {
        openSnackbar({ message: cr.emailSent, variant: "normal" });
        track("corpB2b.onlineRegistration.webEvent", {
          eventName: "CORP_B2B.ONLINE_REGISTRATION.WEB_EVENT",
          eventVersion: "1.0.0",
          pageName: "ORS_PAGE",
          pageCategory: "ORS_PAGE",
          eventValue: "SUBMIT_EMAIL",
          emailAddress: values.email,
        }).send();
      } else if (res.data.status === "EMAIL_HAS_REGISTERED") {
        setError("email", { message: cr.emailHasRegistered });
      }
      setSent(true);
    }

    setLoading(false);
  }

  return (
    <FormProvider {...methods}>
      <View style={styles.sendEmail}>
        <View style={styles.emailInput}>
          <Input
            error={Boolean(error?.message)}
            disabled={loading || sent}
            startIcon={<EmailIcon color={Token.color.bluePrimary} />}
            placeholder={cr.sendEmailPlaceholderV2}
            onChangeText={onChange}
            onBlur={onBlur}
          />
        </View>
        <Button
          disabled={sent}
          loading={loading}
          onPress={formSubmit(handleSubmit)}
          text={cr.sendEmailV2}
          variant="main-cta"
        />
      </View>
      {error?.message && <ErrorMessage text={error.message} />}
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  headline: {
    marginBottom: Token.spacing.m,
  },
  subHeadline: {
    marginBottom: Token.spacing.xxl,
  },
  content: {
    width: 640,
  },
  cardContent: {
    padding: Token.spacing.l,
  },
  registrationCompleteContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Token.spacing.l,
  },
  registrationComplete: {
    marginLeft: Token.spacing.m,
  },
  registrationCompleteInfo: {
    marginTop: Token.spacing.l,
  },
  linkText: {
    textDecorationLine: "underline",
    color: Token.color.bluePrimary,
  },
  seeRequiredDocumentButton: {
    //@ts-expect-error fit-content is not in React Native type
    width: "fit-content",
    marginTop: Token.spacing.m,
    padding: 0,
  },
  sendEmail: {
    marginTop: Token.spacing.l,
    flexDirection: "row",
  },
  emailLabel: {
    marginTop: Token.spacing.l,
  },
  emailInput: {
    marginEnd: Token.spacing.m,
    flex: 1,
  },
  step: {
    marginBottom: Token.spacing.xxl,
  },
  verificationPhaseInfo: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Token.spacing.m,
  },
  verificationPhaseInfoLabel: {
    marginLeft: Token.spacing.m,
  },
  verificationWaitLongerInfo: {
    marginTop: Token.spacing.m,
  },
  info: {
    marginBottom: Token.spacing.l,
  },
  signInButton: {
    marginLeft: "auto",
  },
});
